/**
 * Generic pickers for the 5 org-resource FKs on an Employee record.
 *
 * - <ResourceCombobox>          — Frappe-style searchable input + popover
 *                                 with inline "Create new" + "Advanced
 *                                 search" footer actions.
 * - <QuickAddResourceDialog>    — tiny secondary modal that POSTs the
 *                                 minimum-required fields for a given
 *                                 resource and returns the new row's id.
 * - <ManageResourceDialog>      — list + inline-add + soft-delete table
 *                                 for managing all rows for a company,
 *                                 with a deep link to /org-settings.
 *
 * All three are driven by `org-resource-config.ts` so adding a new lookup
 * resource is one config block, not three components.
 */

import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ChevronDown,
  ExternalLink,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";

import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ErrorMessage } from "@/components/ui/error-message";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { api, type Paginated } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { OrgRef } from "@/lib/types";

import {
  ORG_RESOURCES,
  type OrgResourceKind,
  type QuickAddFieldDef,
  listQueryKey,
  kindToSettingsTab,
} from "./org-resource-config";

/* ──────────────────────────── shared helpers ─────────────────────────── */

function blankPayload(fields: ReadonlyArray<QuickAddFieldDef>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) out[f.name] = f.defaultValue ?? "";
  return out;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

/* ─────────────────────────── ResourceCombobox ────────────────────────── */

export interface ResourceComboboxProps {
  kind: OrgResourceKind;
  companyId: string;
  /** Company short-code, shown in the suffix and the filter chip. */
  companyAbbreviation?: string;
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  placeholder?: string;
  query: UseQueryResult<Paginated<OrgRef>>;
  // FormField cloneElement injects these; forward to the input for a11y.
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  className?: string;
}

/**
 * Searchable single-select with a popover footer that exposes
 * "+ Create a new X" (opens a tiny modal that auto-selects the new row)
 * and "Advanced search" (opens the full Manage dialog).
 * Modeled on the Frappe Awesomplete-style link picker.
 */
export function ResourceCombobox({
  kind,
  companyId,
  companyAbbreviation,
  value,
  onChange,
  disabled,
  placeholder = "Select…",
  query,
  id,
  "aria-describedby": describedBy,
  "aria-invalid": ariaInvalid,
  className,
}: ResourceComboboxProps) {
  const config = ORG_RESOURCES[kind];
  const rows = useMemo(() => query.data?.results ?? [], [query.data]);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showManage, setShowManage] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => rows.find((r) => r.id === value),
    [rows, value],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [rows, search]);

  // Reset highlight when filter changes.
  useEffect(() => {
    setActiveIndex(filtered.length > 0 ? 0 : -1);
  }, [filtered]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Keep the active row scrolled into view.
  useLayoutEffect(() => {
    if (!open || activeIndex < 0) return;
    const li = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    li?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  function commit(rowId: string) {
    onChange(rowId);
    setOpen(false);
    setSearch("");
    inputRef.current?.blur();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && filtered[activeIndex]) {
        e.preventDefault();
        commit(filtered[activeIndex].id);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setSearch("");
      }
    }
  }

  const displayValue = open ? search : selected?.name ?? "";

  return (
    <>
      <div ref={containerRef} className="relative">
        <Input
          ref={inputRef}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          value={displayValue}
          placeholder={placeholder}
          disabled={disabled || query.isLoading}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className={cn("pr-16", className)}
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1 pr-2 text-slate">
          {selected && !disabled ? (
            <button
              type="button"
              aria-label="Clear selection"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange("");
                setSearch("");
                setOpen(false);
              }}
              className="pointer-events-auto rounded p-0.5 hover:bg-slate-100 hover:text-navy"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          ) : null}
          <ChevronDown
            className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
            strokeWidth={2}
            aria-hidden="true"
          />
        </div>

        {open && (
          <div
            className="absolute left-0 right-0 top-full z-50 mt-1 flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
            role="presentation"
          >
            <ul
              ref={listRef}
              role="listbox"
              className="max-h-56 overflow-y-auto py-1"
            >
              {query.isLoading && (
                <li className="px-3 py-2 text-sm text-slate">Loading…</li>
              )}
              {!query.isLoading && filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-slate">
                  {search
                    ? `No ${config.plural} match "${search}"`
                    : `No ${config.plural} yet`}
                </li>
              )}
              {filtered.map((row, idx) => {
                const isActive = idx === activeIndex;
                const isSelected = row.id === value;
                return (
                  <li
                    key={row.id}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(row.id);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={cn(
                      "flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm",
                      isActive ? "bg-blue/10 text-navy" : "text-navy",
                    )}
                  >
                    <span className={cn(isSelected && "font-semibold")}>
                      {row.name}
                      {companyAbbreviation && (
                        <span className="text-slate"> — {companyAbbreviation}</span>
                      )}
                    </span>
                    {isSelected && (
                      <span className="text-[10px] font-medium uppercase tracking-wider text-blue">
                        selected
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            {companyAbbreviation && (
              <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] text-slate">
                Filters applied for Company ={" "}
                <span className="font-semibold text-navy">
                  {companyAbbreviation}
                </span>
              </div>
            )}

            <div className="flex flex-col border-t border-slate-100">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowQuickAdd(true);
                  setOpen(false);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue hover:bg-blue/5"
              >
                <Plus className="h-4 w-4" strokeWidth={2.25} />
                Create a new {capitalize(config.singular)}
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowManage(true);
                  setOpen(false);
                }}
                className="flex items-center gap-2 border-t border-slate-100 px-3 py-2 text-sm text-slate hover:bg-slate-50 hover:text-navy"
              >
                <Search className="h-4 w-4" strokeWidth={2} />
                Advanced search
              </button>
            </div>
          </div>
        )}
      </div>

      {showQuickAdd && (
        <QuickAddResourceDialog
          kind={kind}
          companyId={companyId}
          open={showQuickAdd}
          onClose={() => setShowQuickAdd(false)}
          onCreated={(row) => {
            commit(row.id);
            setShowQuickAdd(false);
          }}
        />
      )}
      {showManage && (
        <ManageResourceDialog
          kind={kind}
          companyId={companyId}
          open={showManage}
          onClose={() => setShowManage(false)}
          onSelect={(rowId) => {
            commit(rowId);
            setShowManage(false);
          }}
        />
      )}
    </>
  );
}

/* ────────────────────── QuickAddResourceDialog ───────────────────────── */

export interface QuickAddResourceDialogProps {
  kind: OrgResourceKind;
  companyId: string;
  open: boolean;
  onClose: () => void;
  onCreated: (row: OrgRef) => void;
}

export function QuickAddResourceDialog({
  kind,
  companyId,
  open,
  onClose,
  onCreated,
}: QuickAddResourceDialogProps) {
  const config = ORG_RESOURCES[kind];
  const queryClient = useQueryClient();
  const toast = useToast();
  const [values, setValues] = useState<Record<string, string>>(() =>
    blankPayload(config.quickAddFields),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);

  useEffect(() => {
    if (!open) {
      setValues(blankPayload(config.quickAddFields));
      setErrors({});
      setSubmitError(null);
    }
  }, [open, config.quickAddFields]);

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post<OrgRef>(config.endpoint, payload),
    onSuccess: (row) => {
      queryClient.invalidateQueries({
        queryKey: listQueryKey(kind, companyId),
      });
      toast.success(`${capitalize(config.singular)} added`, row.name);
      onCreated(row);
    },
    onError: (err) => setSubmitError(err),
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitError(null);
    const nextErrors: Record<string, string> = {};
    for (const f of config.quickAddFields) {
      const v = (values[f.name] ?? "").trim();
      if (f.required && !v) nextErrors[f.name] = `${f.label} is required.`;
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    const payload: Record<string, unknown> = { company: companyId };
    for (const f of config.quickAddFields) {
      const v = values[f.name] ?? "";
      payload[f.name] = v === "" ? null : v;
    }
    create.mutate(payload);
  }

  function update(name: string, value: string) {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  }

  return (
    <Dialog open={open} onClose={onClose} className="max-w-md">
      <form onSubmit={handleSubmit} noValidate>
        <DialogHeader>
          <DialogTitle>New {config.singular}</DialogTitle>
          <DialogDescription>
            Creates the minimum-required fields. Edit the rest from{" "}
            <Link
              to={`/org-settings?tab=${kindToSettingsTab(kind)}`}
              className="font-medium text-blue hover:underline"
            >
              Organization settings
            </Link>{" "}
            once it's created.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          {config.quickAddFields.map((field) => (
            <FieldRenderer
              key={field.name}
              field={field}
              value={values[field.name] ?? ""}
              onChange={(v) => update(field.name, v)}
              error={errors[field.name]}
            />
          ))}
          <ErrorMessage error={submitError} />
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : `Create ${config.singular}`}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/* ──────────────────────── ManageResourceDialog ───────────────────────── */

export interface ManageResourceDialogProps {
  kind: OrgResourceKind;
  companyId: string;
  open: boolean;
  onClose: () => void;
  /** Optional — when set, clicking a row selects it and closes the dialog. */
  onSelect?: (rowId: string) => void;
}

export function ManageResourceDialog({
  kind,
  companyId,
  open,
  onClose,
  onSelect,
}: ManageResourceDialogProps) {
  const config = ORG_RESOURCES[kind];
  const queryClient = useQueryClient();
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");

  const list = useQuery({
    queryKey: listQueryKey(kind, companyId),
    queryFn: () =>
      api.get<Paginated<Record<string, unknown> & OrgRef>>(
        `${config.endpoint}?company=${companyId}&page_size=200`,
      ),
    enabled: open && !!companyId,
  });

  const remove = useMutation({
    mutationFn: (row: OrgRef) => api.delete(`${config.endpoint}${row.id}/`),
    onSuccess: (_data, row) => {
      queryClient.invalidateQueries({ queryKey: listQueryKey(kind, companyId) });
      toast.success(`${capitalize(config.singular)} removed`, row.name);
    },
    onError: (err) =>
      toast.error(
        `Couldn't remove ${config.singular}`,
        err instanceof Error ? err.message : "Unknown error",
      ),
  });

  const allRows = list.data?.results ?? [];
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) => r.name.toLowerCase().includes(q));
  }, [allRows, search]);

  return (
    <>
      <Dialog open={open} onClose={onClose} className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>Find or add {config.singular}</DialogTitle>
              <DialogDescription>
                Browse all {config.plural} for this company. For deeper edits
                (descriptions, defaults, parent / hierarchy, deactivation)
                use the full settings page.
              </DialogDescription>
            </div>
            <Link
              to={`/org-settings?tab=${kindToSettingsTab(kind)}`}
              onClick={onClose}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-navy hover:bg-slate-50"
            >
              Settings
              <ExternalLink className="h-3 w-3" strokeWidth={2} />
            </Link>
          </div>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate"
                strokeWidth={2}
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${config.plural}…`}
                className="pl-9"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => setAddOpen(true)}
              className="gap-1"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              Add new
            </Button>
          </div>

          <p className="text-xs text-slate">
            {list.isLoading
              ? "Loading…"
              : `${filteredRows.length} of ${allRows.length} ${allRows.length === 1 ? config.singular : config.plural}`}
          </p>

          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate">
                <tr>
                  {config.tableColumns.map((col) => (
                    <th key={col.key} className="px-3 py-2 font-semibold">
                      {col.label}
                    </th>
                  ))}
                  <th className="w-20 px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRows.length === 0 && !list.isLoading && (
                  <tr>
                    <td
                      colSpan={config.tableColumns.length + 1}
                      className="px-3 py-6 text-center text-xs text-slate"
                    >
                      {search
                        ? `No matches for "${search}".`
                        : `No ${config.plural} yet. Use "Add new" to create one.`}
                    </td>
                  </tr>
                )}
                {filteredRows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    {config.tableColumns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          "px-3 py-2 text-navy",
                          col.key === "code" && "font-mono text-xs",
                        )}
                      >
                        {formatCell(row[col.key])}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {onSelect && (
                          <button
                            type="button"
                            onClick={() => onSelect(row.id)}
                            className="rounded px-2 py-1 text-[11px] font-medium text-blue hover:bg-blue/5"
                          >
                            Use
                          </button>
                        )}
                        <button
                          type="button"
                          aria-label={`Remove ${row.name}`}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Remove ${config.singular} "${row.name}"? This is a soft-delete.`,
                              )
                            ) {
                              remove.mutate(row);
                            }
                          }}
                          className="rounded p-1 text-slate hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ErrorMessage error={list.error} />
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </Dialog>

      {addOpen && (
        <QuickAddResourceDialog
          kind={kind}
          companyId={companyId}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onCreated={() => setAddOpen(false)}
        />
      )}
    </>
  );
}

/* ─────────────────────────── field renderer ─────────────────────────── */

function FieldRenderer({
  field,
  value,
  onChange,
  error,
}: {
  field: QuickAddFieldDef;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <FormField
      label={field.label}
      required={field.required}
      error={error}
      helperText={field.helper}
    >
      {field.type === "select" ? (
        <Select value={value} onChange={(e) => onChange(e.target.value)}>
          {!field.required && <option value="">(none)</option>}
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={field.mono ? "font-mono" : undefined}
        />
      )}
    </FormField>
  );
}
